// ============================================================
// QrScannerModal — 用 navigator.mediaDevices 開啟相機掃 QR Code
// ------------------------------------------------------------
// 套件：qr-scanner（自帶 web worker，效能好）
// 注意事項：
//   - 必須在 HTTPS 或 localhost 才能取得相機權限
//   - iOS Safari < 14 不支援；本店為 iPad 部署，預期都 ≥ 17
//   - 後鏡頭優先（environment）
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

interface Props {
  onScan: (qrToken: string) => void;
  onClose: () => void;
}

export function QrScannerModal({ onScan, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    let cancelled = false;
    const video = videoRef.current;

    const start = async () => {
      try {
        // 確認瀏覽器支援
        const hasCam = await QrScanner.hasCamera();
        if (!hasCam) {
          setError('找不到相機，請改用 WiFi 或 GPS 打卡');
          return;
        }

        const scanner = new QrScanner(
          video,
          (result) => {
            if (cancelled) return;
            const data = typeof result === 'string' ? result : result.data;
            if (data) {
              // 偵測到後立刻停止，避免重複觸發
              scanner.stop();
              onScan(data);
            }
          },
          {
            preferredCamera: 'environment', // 後鏡頭
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 5,
          }
        );

        scannerRef.current = scanner;
        await scanner.start();
        if (!cancelled) setHasCameraPermission(true);
      } catch (err) {
        if (cancelled) return;
        const msg = (err as Error).message ?? '無法開啟相機';
        if (msg.toLowerCase().includes('permission') || msg.includes('NotAllowed')) {
          setError('相機權限被拒絕，請至瀏覽器設定開放相機權限後再試');
        } else if (msg.toLowerCase().includes('not found') || msg.includes('NotFound')) {
          setError('找不到相機');
        } else {
          setError(`相機無法啟動：${msg}`);
        }
        setHasCameraPermission(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* 標題列 */}
        <div className="flex items-center justify-between px-4 py-3 bg-morandi-50 border-b border-morandi-100">
          <h2 className="text-base font-semibold text-morandi-800">掃描 QR Code</h2>
          <button
            onClick={onClose}
            className="text-morandi-400 hover:text-morandi-600 text-xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-morandi-100"
            aria-label="關閉"
          >×</button>
        </div>

        {/* 相機畫面 */}
        <div className="relative aspect-square bg-black">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />

          {/* 掃描框輔助 */}
          {hasCameraPermission && !error && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-3/5 aspect-square border-2 border-white rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.3)]" />
            </div>
          )}

          {/* 載入中 */}
          {hasCameraPermission === null && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              開啟相機中...
            </div>
          )}

          {/* 錯誤訊息 */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-6 text-center">
              <div>
                <div className="text-3xl mb-3">📷</div>
                <div className="text-white text-sm">{error}</div>
              </div>
            </div>
          )}
        </div>

        {/* 提示 */}
        <div className="px-4 py-3 text-center text-xs text-morandi-500">
          將店內 QR Code 對準框內即可自動偵測
        </div>

        {/* 取消 */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-morandi-100 hover:bg-morandi-200 text-morandi-600 rounded-btn text-sm transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
